import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
  closable?: boolean;
}

function Modal({
  show,
  onHide,
  title,
  children,
  footer,
  size = "md",
  closable = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && show && closable) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleEscape);
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show, onHide, closable]);

  useEffect(() => {
    if (show && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      firstElement?.focus();

      const handleTab = (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener("keydown", handleTab);
      return () => document.removeEventListener("keydown", handleTab);
    }
  }, [show]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closable) {
      onHide();
    }
  };

  if (!show) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    "2xl": "max-w-6xl",
    "3xl": "max-w-7xl",
    "4xl": "max-w-[1440px]",
    "5xl": "max-w-[1600px]",
    full: "max-w-[95vw]",
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={modalRef}
        className={`relative w-full ${sizeClasses[size]} bg-white shadow-xl flex flex-col max-h-[90vh] rounded-xl`}
        aria-labelledby="modal-title"
      >
        {(title || closable) && (
          <div
            className={`flex items-center px-8 ${title ? "justify-between py-6" : "justify-end pt-8 pb-0"}`}
          >
            {title && (
              <h2 id="modal-title" className="text-2xl font-bold text-gray-900">
                {title}
              </h2>
            )}
            {closable && (
              <button
                onClick={onHide}
                className={`text-gray-400 hover:text-gray-600 transition-colors ${!title ? "relative" : ""}`}
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        <div
          className={`px-8 ${title ? "pb-4 pt-1" : "pb-4 pt-8"} overflow-y-auto flex-1`}
        >
          {children}
        </div>

        {footer && (
          <div
            className={`flex items-center px-8 ${title ? "pb-8 pt-2" : "pb-8 pt-4"}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
